import React, { ChangeEvent } from "react";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Stack,
  Button,
} from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  COMPANY_DETAILS,
  EXTERNAL_DESCRIPTION_PREFIX,
  PAGE_MARGIN,
  buildDate,
  buildDateTime,
  buildInvoiceNumber,
  convertNumberToWords,
  ensureSpace,
  ensureUuid,
  formatCurrency,
  formatDate,
  isValidDateValue,
  parseAmount,
  roundCurrency,
  sanitizeFileName,
} from "./common";
import { PlusJakartaSansFontBase64String } from "../../../Helpers/PlusJakartaSans-base64String";

export interface ExternalInvoiceForm {
  billFromName: string;
  billFromAddress: string;
  billFromPan: string;
  documentUuid: string;
  documentDateTime: string;
  invoiceDate: string;
  invoiceNumber: string;
  placeOfSupply: string;
  code: string;
  securityName: string;
  taxableValue: string;
  tdsPercentage: string;
  bankBeneficiaryName: string;
  bankName: string;
  bankAccountNumber: string;
  bankIfsc: string;
}

export interface ExternalCalculations {
  taxableValue: number;
  tdsRate: number;
  tdsAmount: number;
  totalPayable: number;
  amountInWords: string;
}

export const createExternalForm = (): ExternalInvoiceForm => ({
  billFromName: "",
  billFromAddress: "",
  billFromPan: "",
  documentUuid: ensureUuid(),
  documentDateTime: buildDateTime(),
  invoiceDate: buildDate(),
  invoiceNumber: buildInvoiceNumber(),
  placeOfSupply: "Maharashtra",
  code: COMPANY_DETAILS.code,
  securityName: "",
  taxableValue: "",
  tdsPercentage: "2",
  bankBeneficiaryName: "",
  bankName: "",
  bankAccountNumber: "",
  bankIfsc: "",
});

export const calculateExternal = (
  form: ExternalInvoiceForm
): ExternalCalculations => {
  const taxableValue = parseAmount(form.taxableValue);
  const rate = Number.parseFloat(form.tdsPercentage) || 0;

  if (!Number.isFinite(taxableValue)) {
    return {
      taxableValue: Number.NaN,
      tdsRate: rate,
      tdsAmount: Number.NaN,
      totalPayable: Number.NaN,
      amountInWords: "",
    };
  }

  const tdsAmount = roundCurrency((taxableValue * rate) / 100);
  const total = roundCurrency(taxableValue - tdsAmount);

  return {
    taxableValue,
    tdsRate: rate,
    tdsAmount,
    totalPayable: total,
    amountInWords: convertNumberToWords(total),
  };
};

export const validateExternalForm = (form: ExternalInvoiceForm) => {
  const validation: Record<string, string> = {};

  if (!form.billFromName.trim()) validation.billFromName = "Required";
  if (!form.billFromAddress.trim()) validation.billFromAddress = "Required";
  if (!form.billFromPan.trim()) validation.billFromPan = "Required";
  if (!form.documentUuid.trim()) validation.documentUuid = "Required";
  if (!isValidDateValue(form.documentDateTime))
    validation.documentDateTime = "Invalid date";
  if (!isValidDateValue(form.invoiceDate))
    validation.invoiceDate = "Invalid date";
  if (!form.invoiceNumber.trim()) validation.invoiceNumber = "Required";
  if (!form.placeOfSupply.trim()) validation.placeOfSupply = "Required";
  if (!form.securityName.trim()) validation.securityName = "Required";

  const taxable = parseAmount(form.taxableValue);
  if (!Number.isFinite(taxable) || taxable <= 0) {
    validation.taxableValue = "Enter a valid amount";
  }

  const rate = Number.parseFloat(form.tdsPercentage);
  if (!Number.isFinite(rate) || rate < 0) {
    validation.tdsPercentage = "Enter a valid rate";
  }

  if (!form.bankBeneficiaryName.trim())
    validation.bankBeneficiaryName = "Required";
  if (!form.bankName.trim()) validation.bankName = "Required";
  if (!form.bankAccountNumber.trim()) validation.bankAccountNumber = "Required";
  if (!form.bankIfsc.trim()) validation.bankIfsc = "Required";

  return validation;
};

export const generateExternalInvoicePdf = (
  form: ExternalInvoiceForm,
  calculations: ExternalCalculations
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const primaryText = { r: 35, g: 38, b: 45 };
  const mutedText = { r: 107, g: 114, b: 128 };
  const headingText = { r: 15, g: 23, b: 42 };

  doc.addFileToVFS(
    "PlusJakartaSans-Regular.ttf",
    PlusJakartaSansFontBase64String
  );
  doc.addFont("PlusJakartaSans-Regular.ttf", "PlusJakarta", "normal");
  doc.setFont("PlusJakarta", "normal");

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  let cursorY = PAGE_MARGIN;
  const leftColumnWidth = 90;

  doc.setFontSize(12);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text(form.billFromName.trim(), PAGE_MARGIN, cursorY);

  doc.setFontSize(9);
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  doc.text("ORIGINAL FOR RECIPIENT", pageWidth - PAGE_MARGIN, cursorY, {
    align: "right",
  });

  cursorY += 5;
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  const addressLines = doc
    .splitTextToSize(form.billFromAddress.trim(), leftColumnWidth)
    .filter(Boolean);
  addressLines.forEach((line) => {
    doc.text(line, PAGE_MARGIN, cursorY);
    cursorY += 4.5;
  });

  if (form.billFromPan.trim()) {
    doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
    doc.text(`PAN: ${form.billFromPan.trim()}`, PAGE_MARGIN, cursorY);
    cursorY += 8;
  } else {
    cursorY += 4;
  }

  doc.setFontSize(16);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text("TAX INVOICE", pageWidth / 2, cursorY, { align: "center" });
  cursorY += 7;

  doc.setFontSize(9);
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  doc.text("e-Invoice Details", pageWidth / 2, cursorY, { align: "center" });
  cursorY += 8;

  doc.setFontSize(9);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text("Document UUID", PAGE_MARGIN, cursorY);
  doc.text("Document Date", pageWidth - PAGE_MARGIN - 60, cursorY);
  cursorY += 5;

  doc.setFont("PlusJakarta", "normal");
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  doc.text(form.documentUuid, PAGE_MARGIN, cursorY);
  doc.text(
    formatDate(form.documentDateTime, "DD/MM/YYYY HH:mm"),
    pageWidth - PAGE_MARGIN - 60,
    cursorY
  );
  cursorY += 8;

  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text("Bill To", PAGE_MARGIN, cursorY);
  doc.text("Invoice Information", pageWidth - PAGE_MARGIN - 60, cursorY);
  cursorY += 6;

  const billLines = [COMPANY_DETAILS.name, ...COMPANY_DETAILS.addressLines].map(
    (line) => line.trim()
  );
  let billY = cursorY;
  doc.setFontSize(10);
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  doc.text(billLines[0], PAGE_MARGIN, billY);
  billY += 5;
  doc.setFontSize(9);
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  billLines.slice(1).forEach((line) => {
    doc.text(line, PAGE_MARGIN, billY);
    billY += 4.2;
  });

  const infoLabelX = pageWidth - PAGE_MARGIN - 60;
  const infoValueX = pageWidth - PAGE_MARGIN;
  let infoY = cursorY;
  const invoiceInfo: Array<[string, string]> = [
    ["Date", formatDate(form.invoiceDate, "DD/MM/YYYY")],
    ["Invoice Number", form.invoiceNumber],
    ["Place of Supply", form.placeOfSupply],
    ["Code", form.code],
  ];

  invoiceInfo.forEach(([label, value]) => {
    doc.setFontSize(9);
    doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
    doc.text(`${label}:`, infoLabelX, infoY);
    doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
    doc.text(value, infoValueX, infoY, { align: "right" });
    infoY += 4.8;
  });

  cursorY = Math.max(billY, infoY) + 7;

  const description = `${EXTERNAL_DESCRIPTION_PREFIX} ${
    form.securityName || ""
  }`
    .replace(/\s+/g, " ")
    .trim();

  autoTable(doc, {
    startY: cursorY,
    head: [["Description", "Taxable Value"]],
    body: [
      [description, formatCurrency(calculations.taxableValue)],
      [
        `TDS Deduction @ ${calculations.tdsRate}%`,
        formatCurrency(calculations.tdsAmount),
      ],
      ["Total", formatCurrency(calculations.totalPayable)],
    ],
    theme: "grid",
    styles: {
      font: "PlusJakarta",
      fontSize: 8.5,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      textColor: [primaryText.r, primaryText.g, primaryText.b],
    },
    headStyles: {
      font: "PlusJakarta",
      fontSize: 8.5,
      textColor: 255,
      fillColor: [35, 38, 45],
      fontStyle: "bold",
    },
    bodyStyles: {
      textColor: [primaryText.r, primaryText.g, primaryText.b],
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "right" },
    },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });

  cursorY = (doc as any).lastAutoTable.finalY + 10;

  doc.setFont("PlusJakarta", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  const wordsLabel = "Total amount in words:";
  const wordsLabelWidth = doc.getTextWidth(wordsLabel);
  doc.text(wordsLabel, PAGE_MARGIN, cursorY);
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  doc.text(
    calculations.amountInWords || "--",
    PAGE_MARGIN + wordsLabelWidth + 2,
    cursorY
  );
  cursorY += 10;

  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text("Terms and Conditions:", PAGE_MARGIN, cursorY);
  cursorY += 5;
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  doc.text("Payment Terms: Immediate", PAGE_MARGIN, cursorY);
  cursorY += 10;

  doc.setFontSize(9.5);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text(`For ${form.billFromName.trim()}`, PAGE_MARGIN, cursorY);
  cursorY += 16;
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  doc.text("Signatory", PAGE_MARGIN, cursorY);
  cursorY += 5;
  doc.setFontSize(8);
  doc.setFont("PlusJakarta", "italic");
  doc.text(
    "This is an electronically generated invoice and does not require a signature.",
    PAGE_MARGIN,
    cursorY
  );
  doc.setFont("PlusJakarta", "normal");
  cursorY += 10;

  doc.setFontSize(9.5);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text(
    "Bank Information for Direct Deposit and Wire Transfer",
    PAGE_MARGIN,
    cursorY
  );
  cursorY += 7;

  const bankEntries: Array<[string, string]> = [
    ["Beneficiary Name", form.bankBeneficiaryName || "--"],
    ["Bank Name", form.bankName || "--"],
    ["Account Number", form.bankAccountNumber || "--"],
    ["IFSC Code", form.bankIfsc || "--"],
  ];

  const bankLabelWidth = 48;
  bankEntries.forEach(([labelText, valueText]) => {
    cursorY = ensureSpace(doc, cursorY, 8);
    doc.setFontSize(9);
    doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
    doc.text(labelText, PAGE_MARGIN, cursorY);
    doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
    doc.text(valueText, PAGE_MARGIN + bankLabelWidth, cursorY);
    cursorY += 6;
  });

  const disclaimers = [
    "# Discrepancy, if any, noticed in the above Invoice shall be informed to us within fifteen days of the receipt of this Invoice. In case no communication is received within fifteen days, then the same will be treated as accepted by the client.",
    "# As our annual aggregate turnover is less than the mandatory threshold limit prescribed under Goods and Services Tax Act, we are not required to charge GST on the invoice.",
  ];

  const lineHeight = 3.6;
  const wrappedDisclaimers = disclaimers.map((line) =>
    doc.splitTextToSize(line, pageWidth - PAGE_MARGIN * 2)
  );
  const disclaimersHeight =
    wrappedDisclaimers.reduce((sum, lines) => sum + lines.length, 0) *
      lineHeight +
    (wrappedDisclaimers.length - 1) * 2;

  let disclaimersY = pageHeight - PAGE_MARGIN - disclaimersHeight;
  if (disclaimersY < cursorY + 6) {
    disclaimersY = cursorY + 6;
  }

  doc.setFontSize(7.5);
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  wrappedDisclaimers.forEach((lines, index) => {
    doc.text(lines, PAGE_MARGIN, disclaimersY, { lineHeightFactor: 1.25 });
    disclaimersY += lines.length * lineHeight + 1;
  });

  const fileName = `External_Invoice_${sanitizeFileName(
    form.invoiceNumber,
    "invoice"
  )}.pdf`;
  doc.save(fileName);
};

interface ExternalInvoiceFieldsProps {
  form: ExternalInvoiceForm;
  errors: Record<string, string>;
  calculations: ExternalCalculations;
  onChange: (
    field: keyof ExternalInvoiceForm
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onRegenerateUuid: () => void;
  onApplyDefaultInvoiceNumber: () => void;
}

export const ExternalInvoiceFields: React.FC<ExternalInvoiceFieldsProps> = ({
  form,
  errors,
  calculations,
  onChange,
  onRegenerateUuid,
  onApplyDefaultInvoiceNumber,
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <TextField
        label="Billed By - Name"
        value={form.billFromName}
        onChange={onChange("billFromName")}
        fullWidth
        error={Boolean(errors.billFromName)}
        helperText={errors.billFromName}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Billed By - PAN"
        value={form.billFromPan}
        onChange={onChange("billFromPan")}
        fullWidth
        error={Boolean(errors.billFromPan)}
        helperText={errors.billFromPan}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Billed By - Address"
        value={form.billFromAddress}
        onChange={onChange("billFromAddress")}
        fullWidth
        multiline
        minRows={3}
        error={Boolean(errors.billFromAddress)}
        helperText={errors.billFromAddress}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <Stack direction="row" spacing={1}>
        <TextField
          value={form.documentUuid}
          onChange={onChange("documentUuid")}
          label="Document UUID"
          fullWidth
          error={Boolean(errors.documentUuid)}
          helperText={errors.documentUuid}
        />
        <Button variant="outlined" onClick={onRegenerateUuid}>
          Regenerate
        </Button>
      </Stack>
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Document Date & Time"
        type="datetime-local"
        value={form.documentDateTime}
        onChange={onChange("documentDateTime")}
        fullWidth
        InputLabelProps={{ shrink: true }}
        error={Boolean(errors.documentDateTime)}
        helperText={errors.documentDateTime}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Invoice Date"
        type="date"
        value={form.invoiceDate}
        onChange={onChange("invoiceDate")}
        fullWidth
        InputLabelProps={{ shrink: true }}
        error={Boolean(errors.invoiceDate)}
        helperText={errors.invoiceDate}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <Stack direction="row" spacing={1}>
        <TextField
          value={form.invoiceNumber}
          onChange={onChange("invoiceNumber")}
          label="Invoice Number"
          fullWidth
          error={Boolean(errors.invoiceNumber)}
          helperText={errors.invoiceNumber}
        />
        <Button variant="outlined" onClick={onApplyDefaultInvoiceNumber}>
          Apply Default
        </Button>
      </Stack>
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Place of Supply"
        value={form.placeOfSupply}
        onChange={onChange("placeOfSupply")}
        fullWidth
        error={Boolean(errors.placeOfSupply)}
        helperText={errors.placeOfSupply}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Internal Code"
        value={form.code}
        onChange={onChange("code")}
        fullWidth
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Security Name"
        value={form.securityName}
        onChange={onChange("securityName")}
        fullWidth
        error={Boolean(errors.securityName)}
        helperText={errors.securityName}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Taxable Value (INR)"
        value={form.taxableValue}
        onChange={onChange("taxableValue")}
        fullWidth
        error={Boolean(errors.taxableValue)}
        helperText={errors.taxableValue}
        type="number"
        inputProps={{ min: 0, step: "0.01" }}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="TDS Percentage"
        value={form.tdsPercentage}
        onChange={onChange("tdsPercentage")}
        fullWidth
        error={Boolean(errors.tdsPercentage)}
        helperText={errors.tdsPercentage}
        type="number"
        inputProps={{ min: 0, step: "0.01" }}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Beneficiary Name"
        value={form.bankBeneficiaryName}
        onChange={onChange("bankBeneficiaryName")}
        fullWidth
        error={Boolean(errors.bankBeneficiaryName)}
        helperText={errors.bankBeneficiaryName}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Bank Name"
        value={form.bankName}
        onChange={onChange("bankName")}
        fullWidth
        error={Boolean(errors.bankName)}
        helperText={errors.bankName}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Account Number"
        value={form.bankAccountNumber}
        onChange={onChange("bankAccountNumber")}
        fullWidth
        error={Boolean(errors.bankAccountNumber)}
        helperText={errors.bankAccountNumber}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="IFSC Code"
        value={form.bankIfsc}
        onChange={onChange("bankIfsc")}
        fullWidth
        error={Boolean(errors.bankIfsc)}
        helperText={errors.bankIfsc}
      />
    </Grid>
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{ p: 2, bgcolor: "background.default", borderStyle: "dashed" }}
      >
        <Typography variant="subtitle2">Invoice Preview</Typography>
        <Typography variant="body2">
          Taxable Value:{" "}
          {Number.isFinite(calculations.taxableValue)
            ? formatCurrency(calculations.taxableValue)
            : "--"}
        </Typography>
        <Typography variant="body2">
          TDS Amount:{" "}
          {Number.isFinite(calculations.tdsAmount)
            ? formatCurrency(calculations.tdsAmount)
            : "--"}
        </Typography>
        <Typography variant="body2">
          Net Payable:{" "}
          {Number.isFinite(calculations.totalPayable)
            ? formatCurrency(calculations.totalPayable)
            : "--"}
        </Typography>
        <Typography variant="body2">
          Amount in Words: {calculations.amountInWords || "--"}
        </Typography>
      </Paper>
    </Grid>
  </Grid>
);
